import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, Timestamp, query, where, arrayUnion } from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import '../styles/Archives.css';
import '../styles/Questions.css';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

Modal.setAppElement('#root');

const Questions = () => {
  const { classId, termId, subjectId } = useParams();
  const { userData } = useUserAuth();
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [className, setClassName] = useState('');
  const [termName, setTermName] = useState('');
  const [subjectName, setSubjectName] = useState('');
  const [newQuestion, setNewQuestion] = useState({
    questionText: '',
    options: ['', '', '', ''],
    correctIndex: 0
  });
  const [searchQuestion, setSearchQuestion] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [isEditQuestionModalOpen, setIsEditQuestionModalOpen] = useState(false);
  const [isDeleteQuestionModalOpen, setIsDeleteQuestionModalOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [isFetchingQuestions, setIsFetchingQuestions] = useState(true);

  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [isDateDeleteModalOpen, setIsDateDeleteModalOpen] = useState(false);
  const [deleteFromDate, setDeleteFromDate] = useState('');
  const [deleteToDate, setDeleteToDate] = useState('');
  const navigate = useNavigate();

  const cbtCollectionRef = collection(db, "questions");
  const classesCollectionRef = collection(db, "classes");
  const termsCollectionRef = collection(db, "terms");
  const subjectsCollectionRef = collection(db, "subjects");

  useEffect(() => {
    const fetchData = async () => {
      setIsFetchingQuestions(true);
      try {
        // Fetch class name
        const classDoc = await getDocs(query(classesCollectionRef, where('__name__', '==', classId)));
        if (!classDoc.empty) {
          setClassName(classDoc.docs[0].data().name);
        }

        // Fetch term name
        const termDoc = await getDocs(query(termsCollectionRef, where('__name__', '==', termId)));
        if (!termDoc.empty) {
          setTermName(termDoc.docs[0].data().name);
        }

        // Fetch subject name
        const subjectDoc = await getDocs(query(subjectsCollectionRef, where('__name__', '==', subjectId)));
        if (!subjectDoc.empty) {
          setSubjectName(subjectDoc.docs[0].data().name);
        }

        // Fetch questions
        const q = query(
          cbtCollectionRef,
          where('classId', '==', classId),
          where('termId', '==', termId),
          where('subjectId', '==', subjectId)
        );
        const data = await getDocs(q);
        const sets = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
        if (sets.length > 0) {
          setQuestions(sets[0].questions || []);
        } else {
          setQuestions([]);
        }
      } catch (error) {
        console.error("Firestore error:", error);
        setError("Failed to fetch data. Please try again.");
        setShowModal(true);
      } finally {
        setIsFetchingQuestions(false);
      }
    };
    fetchData();
  }, [classId, termId, subjectId]);

  const filteredQuestions = questions.filter(q => 
    q.questionText.toLowerCase().includes(searchQuestion.toLowerCase())
  );

    // Toggle selection of a question
  const toggleQuestionSelection = (index) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedQuestions(newSelected);
  };

  // Delete ALL questions
  const handleDeleteAllQuestions = async () => {
    if (questions.length === 0) return;

    setUpdate(true);
    try {
      const q = query(
        cbtCollectionRef,
        where('classId', '==', classId),
        where('termId', '==', termId),
        where('subjectId', '==', subjectId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docId = querySnapshot.docs[0].id;
        await deleteDoc(doc(db, "questions", docId));
      }

      setQuestions([]);
      setSuccessMessage("All questions deleted successfully");
      setSelectedQuestions(new Set());
    } catch (error) {
      setError("Failed to delete all questions");
      console.error(error);
    } finally {
      setUpdate(false);
      setIsDeleteAllModalOpen(false);
      setIsDateDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  // Delete questions within a date range
  const handleDeleteByDateRange = async () => {
    if (!deleteFromDate || !deleteToDate) {
      setError("Please select both from and to dates");
      setShowModal(true);
      return;
    }

    const fromTimestamp = Timestamp.fromDate(new Date(deleteFromDate));
    const toTimestamp = Timestamp.fromDate(new Date(deleteToDate + "T23:59:59"));

    const questionsToKeep = questions.filter(q => {
      if (!q.createdAt) return true;
      const qDate = q.createdAt.toDate ? q.createdAt.toDate() : new Date(q.createdAt);
      return qDate < fromTimestamp.toDate() || qDate > toTimestamp.toDate();
    });

    setUpdate(true);
    try {
      const q = query(
        cbtCollectionRef,
        where('classId', '==', classId),
        where('termId', '==', termId),
        where('subjectId', '==', subjectId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = doc(db, "questions", querySnapshot.docs[0].id);

        if (questionsToKeep.length === 0) {
          await deleteDoc(docRef);
          setQuestions([]);
        } else {
          await updateDoc(docRef, {
            questions: questionsToKeep,
            updatedAt: Timestamp.now()
          });
          setQuestions(questionsToKeep);
        }
      }

      setSuccessMessage(`Questions from ${deleteFromDate} to ${deleteToDate} deleted successfully`);
      setSelectedQuestions(new Set());
    } catch (error) {
      setError("Failed to delete questions by date range");
      console.error(error);
    } finally {
      setUpdate(false);
      setIsDateDeleteModalOpen(false);
      setDeleteFromDate('');
      setDeleteToDate('');
      setShowModal(true);
    }
  };

  // Delete only selected questions
  const handleDeleteSelected = async () => {
    if (selectedQuestions.size === 0) return;

    const indicesToDelete = Array.from(selectedQuestions).sort((a, b) => b - a); // delete from end
    let updatedQuestions = [...questions];

    indicesToDelete.forEach(index => {
      updatedQuestions.splice(index, 1);
    });

    setUpdate(true);
    try {
      const q = query(
        cbtCollectionRef,
        where('classId', '==', classId),
        where('termId', '==', termId),
        where('subjectId', '==', subjectId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = doc(db, "questions", querySnapshot.docs[0].id);

        if (updatedQuestions.length === 0) {
          await deleteDoc(docRef);
        } else {
          await updateDoc(docRef, {
            questions: updatedQuestions,
            updatedAt: Timestamp.now()
          });
        }
        setQuestions(updatedQuestions);
      }

      setSuccessMessage(`${selectedQuestions.size} selected question(s) deleted successfully`);
      setSelectedQuestions(new Set());
    } catch (error) {
      setError("Failed to delete selected questions");
      console.error(error);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('option')) {
      const index = parseInt(name.slice(6));
      const newOptions = [...newQuestion.options];
      newOptions[index] = value;
      setNewQuestion({ ...newQuestion, options: newOptions });
    } else if (name === 'correctIndex') {
      setNewQuestion({ ...newQuestion, [name]: parseInt(value) });
    } else {
      setNewQuestion({ ...newQuestion, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
  };

  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setImportStatus('Processing file...');
    setImportProgress(0);

    try {
      let questionsData = [];

      if (fileType === 'excel') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        questionsData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        // CSV parsing with better settings
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          trimHeaders: true,
          transformHeader: (header) => header.trim(),
          transform: (value) => (value == null ? '' : value.toString().trim()),
        });
        questionsData = result.data;
      }

      if (questionsData.length === 0) {
        throw new Error("No question data found in the file");
      }

      // ====================== ROBUST FILTER & CLEANING ======================
      const cleanedData = questionsData.filter((row, index) => {
        const qText = (row.questionText || '').toString().trim();
        const optA = (row.optionA || '').toString().trim();
        const optB = (row.optionB || '').toString().trim();
        const optC = (row.optionC || '').toString().trim();
        const optD = (row.optionD || '').toString().trim();
        let correct = (row.correctOption || '').toString().trim().toUpperCase();

        // Accept the row only if core fields exist
        if (!qText || !optA || !optB || !optC || !optD || !correct) {
          console.warn(`Row ${index + 1} skipped - missing required fields`);
          return false;
        }

        // More flexible correctOption handling
        if (!['A', 'B', 'C', 'D', '0', '1', '2', '3'].includes(correct)) {
          // Try to extract A/B/C/D or 0-3 if user wrote "Option A" or "A)" etc.
          const match = correct.match(/[ABCD0-3]/i);
          if (match) {
            correct = match[0].toUpperCase();
          } else {
            console.warn(`Row ${index + 1} skipped - invalid correctOption: "${row.correctOption}"`);
            return false;
          }
        }

        // Store cleaned values back into the row (important!)
        row.questionText = qText;
        row.optionA = optA;
        row.optionB = optB;
        row.optionC = optC;
        row.optionD = optD;
        row.correctOption = correct;

        return true;
      });

      if (cleanedData.length === 0) {
        throw new Error("No valid questions found after cleaning. Please check your file.");
      }

      setImportStatus(`Importing ${cleanedData.length} valid questions...`);

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < cleanedData.length; i++) {
        try {
          const question = cleanedData[i];

          const options = [
            question.optionA,
            question.optionB,
            question.optionC,
            question.optionD
          ];

          let correctIndex;
          const upperCorrect = question.correctOption;

          if (['A', 'B', 'C', 'D'].includes(upperCorrect)) {
            correctIndex = upperCorrect.charCodeAt(0) - 'A'.charCodeAt(0);
          } else {
            correctIndex = parseInt(upperCorrect);
          }

          const qSet = {
            questionText: question.questionText,
            options: options,                    // already trimmed
            correctIndex,
            createdAt: Timestamp.now()
          };

          // === Your original logic (unchanged) ===
          const q = query(
            cbtCollectionRef,
            where('classId', '==', classId),
            where('termId', '==', termId),
            where('subjectId', '==', subjectId)
          );
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const setDoc = querySnapshot.docs[0];
            await updateDoc(doc(db, "questions", setDoc.id), {
              questions: arrayUnion(qSet),
              updatedAt: Timestamp.now()
            });
          } else {
            await addDoc(cbtCollectionRef, {
              classId: classId,
              termId: termId,
              subjectId: subjectId,
              questions: [qSet],
              createdAt: Timestamp.now()
            });
          }

          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing question ${i + 1}:`, error);
        }

        setImportProgress(Math.round(((i + 1) / cleanedData.length) * 100));
      }

      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Bulk import completed: ${successCount} questions added successfully`);
      fetchQuestions();

    } catch (error) {
      console.error("Bulk import error:", error);
      setError(`Bulk import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsBulkImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2500);
    }
  };

  const handleAddQuestion = async () => {
    setError("");
    setSuccessMessage("");

    if (!newQuestion.questionText || newQuestion.options.some(opt => !opt)) {
      setError("All fields are required, including all options.");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      const qSet = { 
        questionText: newQuestion.questionText, 
        options: newQuestion.options, 
        correctIndex: newQuestion.correctIndex,
        createdAt: Timestamp.now()
      };

      const q = query(
        cbtCollectionRef,
        where('classId', '==', classId),
        where('termId', '==', termId),
        where('subjectId', '==', subjectId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const setDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, "questions", setDoc.id), {
          questions: arrayUnion(qSet),
          updatedAt: Timestamp.now()
        });
      } else {
        await addDoc(cbtCollectionRef, {
          classId: classId,
          termId: termId,
          subjectId: subjectId,
          questions: [qSet],
          createdAt: Timestamp.now()
        });
      }

      setSuccessMessage("Question added successfully");
      fetchQuestions();
      setNewQuestion({
        questionText: '',
        options: ['', '', '', ''],
        correctIndex: 0
      });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add question");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditQuestion = async () => {
    setUpdate(true);
    try {
      const updatedQuestions = [...questions];
      updatedQuestions[currentQuestionIndex] = {
        questionText: newQuestion.questionText,
        options: newQuestion.options,
        correctIndex: newQuestion.correctIndex,
        createdAt: questions[currentQuestionIndex].createdAt // Preserve original createdAt
      };

      const q = query(
        cbtCollectionRef,
        where('classId', '==', classId),
        where('termId', '==', termId),
        where('subjectId', '==', subjectId)
      );
      const querySnapshot = await getDocs(q);
      const setDoc = querySnapshot.docs[0];

      await updateDoc(doc(db, "questions", setDoc.id), {
        questions: updatedQuestions,
        updatedAt: Timestamp.now()
      });

      setSuccessMessage("Question updated successfully");
      fetchQuestions();
      setIsEditQuestionModalOpen(false);
    } catch (error) {
      setError("Failed to update question");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleDeleteQuestion = async () => {
    setUpdate(true);
    try {
      const updatedQuestions = [...questions];
      updatedQuestions.splice(currentQuestionIndex, 1);

      const q = query(
        cbtCollectionRef,
        where('classId', '==', classId),
        where('termId', '==', termId),
        where('subjectId', '==', subjectId)
      );
      const querySnapshot = await getDocs(q);
      const setDoc = querySnapshot.docs[0];

      await updateDoc(doc(db, "questions", setDoc.id), {
        questions: updatedQuestions
      });

      if (updatedQuestions.length === 0) {
        await deleteDoc(doc(db, "questions", setDoc.id));
      }

      setSuccessMessage("Question deleted successfully");
      fetchQuestions();
      setIsDeleteQuestionModalOpen(false);
    } catch (error) {
      setError("Failed to delete question");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const fetchQuestions = async () => {
    setIsFetchingQuestions(true);
    try {
      const q = query(
        cbtCollectionRef,
        where('classId', '==', classId),
        where('termId', '==', termId),
        where('subjectId', '==', subjectId)
      );
      const data = await getDocs(q);
      const sets = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      if (sets.length > 0) {
        setQuestions(sets[0].questions || []);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error("Firestore error:", error);
      setError("Failed to fetch questions. Please try again.");
      setShowModal(true);
    } finally {
      setIsFetchingQuestions(false);
    }
  };

  const openEditQuestionModal = (question, index) => {
    setCurrentQuestionIndex(index);
    setNewQuestion({
      questionText: question.questionText,
      options: [...question.options],
      correctIndex: question.correctIndex
    });
    setIsEditQuestionModalOpen(true);
  };

  const openDeleteQuestionModal = (index) => {
    setCurrentQuestionIndex(index);
    setIsDeleteQuestionModalOpen(true);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}
      {isFetchingQuestions ? (
        <p className="male-female-stat align-center justify-center">Loading ...</p>
      ) : !allowedRoles.includes(userData?.status) ? (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      ) : (
        <>
          <div className="top-elements row align-center space-between">
            <button onClick={() => navigate(-1)} className="back-link back-link1">
              <i className="fa-solid fa-arrow-left size3"></i>
            </button>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" className="home-link" onClick={() => window.location.reload()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>
          <div className="login-page-items column align-center scroll-fixed">
            <div className='breadcrumb'>
              <button onClick={() => navigate(-3)} className="back-link back-link1 back-link2">
                Class &nbsp;&nbsp;&nbsp;<i className="fa-solid fa-angle-right font-gold size4"></i>
              </button>
              <button onClick={() => navigate(-2)} className="back-link back-link1 back-link2">
                Term &nbsp;&nbsp;&nbsp;<i className="fa-solid fa-angle-right font-gold size4"></i>
              </button>
              <button onClick={() => navigate(-1)} className="back-link back-link1 back-link2">
                Subject &nbsp;&nbsp;&nbsp;<i className="fa-solid fa-angle-right font-gold size4"></i>
              </button>
              <button className="back-link back-link1 back-link2">
                Questions
              </button>
            </div>
            <h2 className="manage-page-heading size4">
              {className || 'Loading...'}/{termName || 'Loading...'} term/{subjectName || 'Loading...'}
            </h2>
            <div className="row justify-center align-center gap-15 add-buttons">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>
                Add Question
              </button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>
                Bulk Import
              </button>
              
              {questions.length > 0 && (
                <>                  
                  <button 
                    className="login-button login-button-d caps danger-btn"
                    onClick={() => setIsDateDeleteModalOpen(true)}
                  >
                    Delete
                  </button>

                  {selectedQuestions.size > 0 && (
                    <button 
                      className="login-button login-button-d caps danger-btn"
                      onClick={handleDeleteSelected}
                    >
                      Delete Selected ({selectedQuestions.size})
                    </button>
                  )}
                </>
              )}
            </div>
            <input
              type="text"
              placeholder="Search by question text"
              className="input-field1"
              value={searchQuestion}
              onChange={(e) => setSearchQuestion(e.target.value)}
            />
            <div className="scroll-fixed scroll-fixed2">
              <div className="class-list class-list-d scrollable">
                {filteredQuestions.length === 0 ? (
                  <p className="male-female-stat">No questions found. Add a question to create a set.</p>
                ) : (
                  filteredQuestions.map((q, index) => (                    
                    <div key={index} className="class-item student-item row">
                      <div className="question-item-details column">                         
                        <p className="text-wrap">
                          <input
                            type="checkbox"
                            checked={selectedQuestions.has(index)}
                            onChange={() => toggleQuestionSelection(index)}
                            style={{marginRight: '12px', transform: 'scale(1.2)'}}
                          />
                          <strong>Q{index + 1}:</strong>
                          {q.questionText}
                        </p>
                        <ul style={{listStyle: 'none', paddingLeft: '10px'}}>
                          {q.options.map((opt, i) => (
                            <li key={i}>
                              {String.fromCharCode(65 + i)}. {opt}{" "}
                              {i === q.correctIndex ? <i className="fa-solid fa-check font-red"></i> : ""}
                            </li>
                          ))}
                        </ul>                        

                        <div className="row align-center justify-center gap-15 questions-action-buttons">
                          <span className="login-button cbt-login-button size5 text-center">
                            {formatDate(q.createdAt)}
                          </span>
                          <button onClick={() => openEditQuestionModal(q, index)} className="login-button caps">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button onClick={() => openDeleteQuestionModal(index)} className="login-button caps">
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Question Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Question"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Question</h2>
        <form className="student-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <textarea
            name="questionText"
            className="input-field"
            placeholder="Question Text"
            value={newQuestion.questionText}
            onChange={handleInputChange}
          />
          {newQuestion.options.map((opt, i) => (
            <div key={i} className="row align-center">
              <input
                type="text"
                name={`option${i}`}
                className="input-field"
                placeholder={`Option ${String.fromCharCode(65 + i)}`}
                value={opt}
                onChange={handleInputChange}
                style={{ flex: 1 }}
              />
              <input
                type="radio"
                name="correctIndex"
                value={i}
                checked={newQuestion.correctIndex === i}
                onChange={handleInputChange}
              />
            </div>
          ))}
          <button type="button" className="login-button caps" onClick={handleAddQuestion}>
            Add Question
          </button>
          <button type="button" className="login-button caps" onClick={() => setIsAddModalOpen(false)}>
            Cancel
          </button>
        </form>
      </Modal>

      {/* Edit Question Modal */}
      <Modal
        isOpen={isEditQuestionModalOpen}
        onRequestClose={() => setIsEditQuestionModalOpen(false)}
        contentLabel="Edit Question"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Question</h2>
        <form className="student-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <textarea
            name="questionText"
            className="input-field"
            placeholder="Question Text"
            value={newQuestion.questionText}
            onChange={handleInputChange}
          />
          {newQuestion.options.map((opt, i) => (
            <div key={i} className="row align-center">
              <input
                type="text"
                name={`option${i}`}
                className="input-field"
                placeholder={`Option ${String.fromCharCode(65 + i)}`}
                value={opt}
                onChange={handleInputChange}
                style={{ flex: 1 }}
              />
              <input
                type="radio"
                name="correctIndex"
                value={i}
                checked={newQuestion.correctIndex === i}
                onChange={handleInputChange}
              />
            </div>
          ))}
          <button type="button" className="login-button caps" onClick={handleEditQuestion}>
            Save Changes
          </button>
          <button type="button" className="login-button caps" onClick={() => setIsEditQuestionModalOpen(false)}>
            Cancel
          </button>
        </form>
      </Modal>

      {/* Delete Question Modal */}
      <Modal
        isOpen={isDeleteQuestionModalOpen}
        onRequestClose={() => setIsDeleteQuestionModalOpen(false)}
        contentLabel="Delete Question"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Question</h2>
        <div>
          <p className="male-female-stat">Are you sure you want to delete this question?</p>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsDeleteQuestionModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleDeleteQuestion}>Delete</button>
          </div>
        </div>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
          setImportProgress(0);
          setImportStatus('');
        }}
        contentLabel="Bulk Import Questions"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Questions</h2>
        <div className="modal-form">
          <div style={{marginBottom: '5px'}}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          {importStatus && (
            <div>
              <p>{importStatus}</p>
              {importProgress > 0 && (
                <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}>
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '20px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '5px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      color: 'white'
                    }}
                  >
                    {importProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="row align-center justify-center gap-15">
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
                setImportProgress(0);
                setImportStatus('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={processBulkImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED FIELDS:</strong> questionText, optionA, optionB, optionC, optionD, correctOption (A/B/C/D or 0/1/2/3)</p>
            <p><strong>FOR EXCEL:</strong> First row should contain column headers</p>
            <p><strong>FOR CSV:</strong> First row should contain column headers, comma-separated</p>
          </div>
        </div>
      </Modal>    

      {/* Delete by Date Range Modal */}
      <Modal
        isOpen={isDateDeleteModalOpen}
        onRequestClose={() => setIsDateDeleteModalOpen(false)}
        contentLabel="Delete Questions by Date"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Questions by Date Range</h2>
        <div className="modal-form">
          <label>From Date:</label>
          <input
            type="date"
            value={deleteFromDate}
            onChange={(e) => setDeleteFromDate(e.target.value)}
            className="input-field1"
          />

          <label>To Date:</label>
          <input
            type="date"
            value={deleteToDate}
            onChange={(e) => setDeleteToDate(e.target.value)}
            className="input-field1"
          />          

          <div className="row align-center gap-1" style={{marginTop: '20px'}}>
            <button className="login-button" onClick={() => setIsDateDeleteModalOpen(false)}>Cancel</button>
            <button className="login-button" style={{backgroundColor: '#fc796f', color: '#fff'}} onClick={handleDeleteByDateRange}>
              Delete Questions in Range
            </button>
          </div>
          <button 
            className="login-button login-button-d caps danger-btn"
            onClick={() => setIsDeleteAllModalOpen(true)}
          >
            Delete All The Questions
          </button>
        </div>
      </Modal>

      {/* Delete All Modal */}
      <Modal
        isOpen={isDeleteAllModalOpen}
        onRequestClose={() => setIsDeleteAllModalOpen(false)}
        contentLabel="Delete All Questions"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete All Questions</h2>
        <p className="male-female-stat font-red">
          This action will permanently delete <strong>ALL {questions.length}</strong> questions.<br />
          This cannot be undone.
        </p>
        <div className="row align-center gap-1">
          <button className="login-button" onClick={() => setIsDeleteAllModalOpen(false)}>Cancel</button>
          <button className="login-button" style={{backgroundColor: '#fc796f', color: '#fff'}} onClick={handleDeleteAllQuestions}>
            Yes, Delete All
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Questions;